// TikTokAccountsCard — the multi-token "TikTok accounts" management card.
//
// TikTok grants FINANCE access per TikTok USER, not per Business Center, so a
// single authorized identity usually cannot read every account's balance. The
// backend therefore keeps up to 8 TOKEN SLOTS, each holding one authorized
// TikTok identity's token. This card is the operator surface over them:
//
//   • slot table (GET /admin/tiktok/slots) with inline label edit, per-slot
//     authorize / re-authorize, an authorized-advertiser read-back, and
//     enable/disable;
//   • balance coverage (GET /admin/tiktok/coverage) — which BC accounts NO
//     active slot can read, i.e. which finance manager still has to authorize,
//     PLUS the finance split: an account can be reachable ("covered") and still
//     have an unreadable BALANCE, because TikTok grants finance separately. The
//     Business Centers that answered 40002 "no finance permission" to a slot are
//     listed explicitly — that refusal used to fail silently and just left the
//     balance column blank.
//
// Nothing here re-queries TikTok on demand: the advertiser and coverage reads
// both serve what the backend has already recorded. New grants reach this card
// through the consent flow, not through a button.
//
// SECURITY: no token value is ever displayed, requested, or held here. The
// consent flow only opens a server-built URL; the code exchange and the Secret
// Manager write happen entirely server-side. `last_error` is already sanitized
// by the backend (a reason code, never a raw upstream body) and is rendered
// as-is. Enable/disable and label writes go ONLY to the authorized admin
// endpoints, which re-check the admin role — the buttons are an affordance,
// not the gate.

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import type { ApiError } from "@/api/client";
import {
  getTokenCoverage,
  listSlotAdvertisers,
  listTokenSlots,
  reportsBalanceReadability,
  startSlotAuth,
  updateTokenSlot,
  PRIMARY_SLOT,
  type CoverageAccount,
  type TokenCoverage,
  type TokenSlot,
  type TokenSlotStatus,
} from "@/api/tiktok";
import { errMessage } from "@/lib/errMessage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DataTable,
  Modal,
  StatusPill,
  ToneBadge,
  type Column,
  type Tone,
} from "@/components/nexus";

// Structured refusals this surface can provoke -> an operator-facing sentence
// (diagnosis + next step). Anything else falls through to errMessage().
const SLOT_REASONS: Record<string, string> = {
  primary_slot:
    "Slot 1 is the primary TikTok identity and cannot be disabled — re-authorize it instead.",
  slot_empty:
    "That slot has no TikTok account yet — authorize one into it first.",
  slot_invalid: "That slot number does not exist — the backend serves slots 1-8.",
  status_invalid:
    "That is not a status the backend accepts — a slot can only be made active or disabled.",
  slot_not_wired:
    "That slot has no server-side token store yet — it has to be wired in the backend deploy before it can be authorized.",
};

// The /admin/tiktok/* routes refuse through the backend's writeError, which
// renders {"error": "<code>"} — so the reason code arrives under `error`, not
// under `reason`. `reason` is still read as a fallback because the richer
// {error, reason} envelope is what the REST of the admin API uses, and reading
// both costs nothing if this surface ever moves to it.
function slotErrMessage(err: unknown): string {
  const body = (err as ApiError)?.body as
    | { error?: unknown; reason?: unknown }
    | undefined;
  for (const code of [body?.error, body?.reason]) {
    if (typeof code === "string" && SLOT_REASONS[code]) {
      return SLOT_REASONS[code];
    }
  }
  return errMessage(err);
}

const STATUS_TONE: Record<TokenSlotStatus, Tone> = {
  active: "credit",
  disabled: "warn",
  empty: "na",
};

// RFC3339 UTC string -> local display; "" (unset) renders an em-dash.
function whenLabel(ts: string): string {
  if (!ts) return "—";
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

// ---- inline label editor ------------------------------------------------------

function LabelCell({
  slot,
  onSave,
}: {
  slot: TokenSlot;
  onSave: (slot: number, label: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(slot.label);
  const [saving, setSaving] = useState(false);

  function begin() {
    setValue(slot.label);
    setEditing(true);
  }

  async function commit() {
    if (saving) return;
    const next = value.trim();
    if (next === slot.label) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(slot.slot, next);
      setEditing(false);
    } catch {
      // The caller already surfaced the sanitized error; keep the editor open
      // so the operator can retry or cancel.
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={begin}
        aria-label={`Edit label for slot ${slot.slot}`}
        className="rounded px-1 text-left text-sm text-nx-text hover:bg-nx-bg-2"
      >
        {slot.label || <span className="text-nx-text-3">Add a label</span>}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <Input
        autoFocus
        value={value}
        aria-label={`Label for slot ${slot.slot}`}
        placeholder="e.g. Finance manager — A&A"
        className="h-7 w-44"
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void commit();
          if (e.key === "Escape") setEditing(false);
        }}
      />
      <Button type="button" size="xs" disabled={saving} onClick={() => void commit()}>
        {saving ? "Saving…" : "Save"}
      </Button>
      <Button
        type="button"
        size="xs"
        variant="ghost"
        disabled={saving}
        onClick={() => setEditing(false)}
      >
        Cancel
      </Button>
    </div>
  );
}

// ---- coverage ------------------------------------------------------------------

// One account row inside a coverage list: the advertiser id (mono, the thing an
// operator pastes into TikTok) then the name, or an em-dash when unnamed.
function CoverageRow({ account }: { account: CoverageAccount }) {
  return (
    <li className="flex flex-wrap items-center gap-2 rounded-md bg-nx-bg px-2 py-1">
      <code className="font-mono text-xs text-nx-text-2 tabular-nums">
        {account.advertiser_id}
      </code>
      <span className="text-xs text-nx-text-3">{account.name || "—"}</span>
      {/* balance_slots is the finance-read evidence: empty means no slot has
          ever completed a finance read for this advertiser. */}
      {(account.balance_slots?.length ?? 0) > 0 && (
        <span className="text-xs text-nx-text-3">
          finance slots {account.balance_slots?.join(", ")}
        </span>
      )}
    </li>
  );
}

// A collapsible list of accounts (collapsed by default — these lists can run to
// dozens of rows and must not push the slot table off screen).
function CoverageList({
  label,
  accounts,
}: {
  label: string;
  accounts: CoverageAccount[];
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="grid gap-2">
      <Button
        type="button"
        size="xs"
        variant="outline"
        className="w-fit"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? "Hide" : "Show"} {label} ({accounts.length})
      </Button>
      {open && (
        <ul className="grid gap-1">
          {accounts.map((a) => (
            <CoverageRow key={a.advertiser_id} account={a} />
          ))}
        </ul>
      )}
    </div>
  );
}

function CoverageBlock({
  coverage,
  loading,
  error,
  onRefresh,
}: {
  coverage: TokenCoverage | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}) {
  // The backend reports EVERY cached account with a `covered` flag; the
  // uncovered list is that flag filtered, never a separate server field.
  const accounts = coverage?.accounts ?? [];
  const uncovered = accounts.filter((a) => !a.covered);
  const fullyCovered =
    coverage !== null && coverage.summary.total > 0 && uncovered.length === 0;

  // Only split reachability from finance-readability when THIS backend reports
  // the finance fields (see reportsBalanceReadability) — otherwise an older
  // deploy would render every account as "balance not readable".
  const financeAware = coverage !== null && reportsBalanceReadability(coverage);
  // Reachable, but no slot has a finance read for it: the account resolves, the
  // balance stays blank. `!== true` (not `=== false`) so an account that omits
  // the flag on a finance-aware backend is treated as not-yet-readable.
  const balanceBlind = financeAware
    ? accounts.filter((a) => a.covered && a.balance_readable !== true)
    : [];
  const balanceReadable = coverage?.summary.balance_readable ?? 0;
  const deniedBCs = coverage?.denied_bcs ?? [];

  return (
    <div className="grid gap-3 rounded-lg border border-nx-border bg-nx-bg-2 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-medium tracking-wide text-nx-text-2 uppercase">
          Balance coverage
        </span>
        <Button
          type="button"
          size="xs"
          variant="outline"
          disabled={loading}
          onClick={onRefresh}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      {error && (
        <p className="rounded-md bg-nx-danger-soft px-3 py-2 text-xs text-nx-danger">
          Could not load coverage: {error}
        </p>
      )}

      {loading && !coverage ? (
        <Skeleton className="h-10 w-full" />
      ) : coverage ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill
              tone={
                fullyCovered &&
                (!financeAware ||
                  balanceReadable >= coverage.summary.total) &&
                deniedBCs.length === 0
                  ? "credit"
                  : "warn"
              }
            >
              <span className="text-sm font-medium text-nx-text tabular-nums">
                {financeAware ? (
                  <>
                    reachable {coverage.summary.covered} / finance-readable{" "}
                    {balanceReadable} of {coverage.summary.total} accounts
                  </>
                ) : (
                  <>
                    {coverage.summary.covered} of {coverage.summary.total}{" "}
                    accounts covered
                  </>
                )}
              </span>
            </StatusPill>
          </div>

          <p className="max-w-2xl text-xs text-nx-text-3">
            Uncovered = no authorized TikTok identity can read this account's
            balance — authorize the finance manager who owns it.
            {financeAware && (
              <>
                {" "}
                Reachable is not the same as finance-readable: TikTok grants the
                finance role separately, so an account can resolve and still
                report a blank balance.
              </>
            )}
          </p>

          {/* The silent 40002: a Business Center that refuses finance reads to a
              slot. Rendered per BC because the fix is per BC — grant that slot's
              identity the finance role there. */}
          {deniedBCs.map((bc) => (
            <p
              key={bc.bc_id}
              title={
                bc.last_seen ? `last refused ${whenLabel(bc.last_seen)}` : undefined
              }
              className="rounded-md bg-nx-warn-soft px-3 py-2 text-xs text-nx-warn"
            >
              Finance role missing in BC {bc.bc_id} (
              {(bc.slots ?? []).length === 1 ? "slot" : "slots"}{" "}
              {(bc.slots ?? []).join(", ") || "—"}) — balances for its accounts
              are blank
            </p>
          ))}

          {uncovered.length > 0 ? (
            <CoverageList label="uncovered" accounts={uncovered} />
          ) : (
            <p className="text-xs text-nx-text-3">
              Every known account is readable by at least one active TikTok
              account.
            </p>
          )}

          {/* Reachable but balance NOT readable — the accounts an operator
              would otherwise chase as "missing balance" with no explanation. */}
          {balanceBlind.length > 0 && (
            <CoverageList
              label="reachable but balance NOT readable"
              accounts={balanceBlind}
            />
          )}
        </>
      ) : null}
    </div>
  );
}

// ---- card ----------------------------------------------------------------------

export function TikTokAccountsCard() {
  const [slots, setSlots] = useState<TokenSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The slot whose action is currently in flight (disables its buttons).
  const [busySlot, setBusySlot] = useState<number | null>(null);
  const [confirmDisable, setConfirmDisable] = useState<TokenSlot | null>(null);

  const [coverage, setCoverage] = useState<TokenCoverage | null>(null);
  const [coverageLoading, setCoverageLoading] = useState(true);
  const [coverageError, setCoverageError] = useState<string | null>(null);

  const loadSlots = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSlots(await listTokenSlots());
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCoverage = useCallback(async () => {
    setCoverageLoading(true);
    setCoverageError(null);
    try {
      setCoverage(await getTokenCoverage());
    } catch (err) {
      setCoverageError(errMessage(err));
    } finally {
      setCoverageLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSlots();
    void loadCoverage();
  }, [loadSlots, loadCoverage]);

  // Replace one row with the backend's authoritative updated slot object.
  //
  // advertiser_count is deliberately EXCLUDED: the update endpoint does not
  // compute it and always reports 0, so spreading it in would silently blank a
  // real count the list endpoint had already given us.
  function mergeSlot(updated: TokenSlot) {
    setSlots((prev) =>
      prev.map((s) =>
        s.slot === updated.slot
          ? { ...s, ...updated, advertiser_count: s.advertiser_count }
          : s,
      ),
    );
  }

  async function saveLabel(slot: number, label: string) {
    try {
      mergeSlot(await updateTokenSlot(slot, { label }));
      toast.success(`Slot ${slot} label updated`);
    } catch (err) {
      toast.error(slotErrMessage(err));
      throw err;
    }
  }

  // Open the server-built consent page for ONE slot. The token is exchanged and
  // stored server-side; this browser only ever handles the URL.
  async function authorize(slot: number) {
    if (busySlot !== null) return;
    setBusySlot(slot);
    try {
      const { authorize_url } = await startSlotAuth(slot);
      window.open(authorize_url, "_blank", "noopener");
    } catch (err) {
      toast.error(slotErrMessage(err));
    } finally {
      setBusySlot(null);
    }
  }

  // "Add TikTok account" = authorize into the first EMPTY slot.
  async function addAccount() {
    const empty = slots.find((s) => s.status === "empty");
    if (!empty) {
      toast.error(
        "Every TikTok token slot is in use — re-authorize an existing slot instead.",
      );
      return;
    }
    await authorize(empty.slot);
  }

  // Read back the slot's STORED authorized advertiser set and reconcile the
  // row's count with it. This does NOT re-query TikTok — the backend has no
  // such route — so it can only confirm what the server already knows; a grant
  // added on TikTok's side appears after a re-authorize or a successful probe.
  async function checkAdvertisers(slot: number) {
    if (busySlot !== null) return;
    setBusySlot(slot);
    try {
      const res = await listSlotAdvertisers(slot);
      const count = res.advertisers.length;
      setSlots((prev) =>
        prev.map((s) =>
          s.slot === res.slot ? { ...s, advertiser_count: count } : s,
        ),
      );
      toast.success(
        `Slot ${res.slot}: ${count} authorized advertiser${count === 1 ? "" : "s"}`,
      );
      void loadCoverage();
    } catch (err) {
      toast.error(slotErrMessage(err));
    } finally {
      setBusySlot(null);
    }
  }

  async function setStatus(slot: number, status: "active" | "disabled") {
    if (busySlot !== null) return;
    setBusySlot(slot);
    try {
      const updated = await updateTokenSlot(slot, { status });
      mergeSlot(updated);
      toast.success(
        status === "disabled"
          ? `Slot ${slot} disabled — its accounts are no longer read`
          : `Slot ${slot} enabled`,
      );
      setConfirmDisable(null);
      void loadCoverage();
    } catch (err) {
      toast.error(slotErrMessage(err));
    } finally {
      setBusySlot(null);
    }
  }

  const columns: Column<TokenSlot>[] = [
    {
      key: "slot",
      header: "Slot",
      cell: (s) => (
        <span className="font-mono text-xs text-nx-text-2 tabular-nums">
          #{s.slot}
          {s.slot === PRIMARY_SLOT && (
            <span className="ml-1 text-nx-text-3">primary</span>
          )}
        </span>
      ),
    },
    {
      key: "label",
      header: "Label",
      cell: (s) => <LabelCell slot={s} onSave={saveLabel} />,
    },
    {
      key: "status",
      header: "Status",
      cell: (s) => (
        <ToneBadge tone={STATUS_TONE[s.status] ?? "na"}>{s.status}</ToneBadge>
      ),
    },
    {
      key: "advertisers",
      header: "Advertisers",
      align: "right",
      cell: (s) => (
        <span className="text-sm text-nx-text-2 tabular-nums">
          {s.status === "empty" ? "—" : s.advertiser_count}
        </span>
      ),
    },
    {
      key: "last_ok",
      header: "Last success",
      align: "right",
      cell: (s) => (
        <span className="text-xs text-nx-text-3 tabular-nums">
          {whenLabel(s.last_ok_at)}
        </span>
      ),
    },
    {
      key: "last_error",
      header: "Last error",
      cell: (s) =>
        s.last_error ? (
          <span
            title={s.last_error}
            className="block max-w-[14rem] truncate text-xs text-nx-danger"
          >
            {s.last_error}
          </span>
        ) : (
          <span className="text-nx-text-3">—</span>
        ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (s) => {
        const busy = busySlot === s.slot;
        return (
          <div className="flex flex-wrap justify-end gap-1">
            <Button
              type="button"
              size="xs"
              variant={s.status === "empty" ? "default" : "outline"}
              disabled={busy}
              onClick={() => void authorize(s.slot)}
            >
              {s.status === "empty" ? "Authorize" : "Re-authorize"}
            </Button>
            {s.status !== "empty" && (
              <Button
                type="button"
                size="xs"
                variant="outline"
                disabled={busy}
                title="Show how many advertisers this slot is currently authorized for"
                onClick={() => void checkAdvertisers(s.slot)}
              >
                Check advertisers
              </Button>
            )}
            {s.status === "active" && (
              <Button
                type="button"
                size="xs"
                variant="destructive"
                disabled={busy}
                onClick={() => setConfirmDisable(s)}
              >
                Disable
              </Button>
            )}
            {s.status === "disabled" && (
              <Button
                type="button"
                size="xs"
                variant="outline"
                disabled={busy}
                onClick={() => void setStatus(s.slot, "active")}
              >
                Enable
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>TikTok accounts</CardTitle>
        <CardDescription>
          TikTok grants finance access per TikTok USER, so one authorized
          identity usually cannot read every account's balance. Each slot holds
          one authorized TikTok account's server-held token — slot 1 is the
          original primary. Tokens are exchanged and stored entirely
          server-side; they never touch this browser.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            disabled={loading || busySlot !== null}
            onClick={() => void addAccount()}
          >
            Add TikTok account
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={loading}
            onClick={() => void loadSlots()}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </Button>
          <p className="max-w-xl text-xs text-nx-text-3">
            Opens TikTok's consent page for the next free slot. The token is
            stored server-side and never touches this browser; new balances
            appear within ~5 minutes. Complete the consent within 10 minutes —
            the request expires after that.
          </p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertTitle>Could not load TikTok accounts</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading && slots.length === 0 ? (
          <div className="grid gap-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={slots}
            rowKey={(s) => `slot-${s.slot}`}
            empty="No TikTok token slots reported by the backend."
          />
        )}

        <CoverageBlock
          coverage={coverage}
          loading={coverageLoading}
          error={coverageError}
          onRefresh={() => void loadCoverage()}
        />
      </CardContent>

      {/* Disabling a slot removes its accounts from every balance read — a
          high-trust change, so it is confirm-gated (the backend re-checks). */}
      <Modal
        open={confirmDisable !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmDisable(null);
        }}
        title={`Disable slot ${confirmDisable?.slot ?? ""}?`}
        description={
          <>
            {confirmDisable?.label
              ? `“${confirmDisable.label}” `
              : "This TikTok account "}
            stops being used for balance reads, so its{" "}
            {confirmDisable?.advertiser_count ?? 0} advertiser
            {confirmDisable?.advertiser_count === 1 ? "" : "s"} may become
            uncovered. The stored token is kept — you can enable the slot again
            later.
          </>
        }
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmDisable(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={busySlot !== null}
              onClick={() =>
                void setStatus(confirmDisable?.slot ?? 0, "disabled")
              }
            >
              Confirm
            </Button>
          </>
        }
      />
    </Card>
  );
}
